import React from 'react';
import { twMerge } from 'tailwind-merge';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import WebUiPageWrapper from '../../components/page-wrapper';

type StpPortState = { port: string | number; state: string; role: string };
type StpVersion = { lan?: string; lanprio?: number };
type RootBridge = { rootbridge?: string };

export default function WebUiStatusStpPage() {
  const [version, setVersion] = React.useState<StpVersion>({});
  const [root, setRoot] = React.useState('');
  const [ports, setPorts] = React.useState<StpPortState[]>([]);
  const [error, setError] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    const ver = await postWebUiApi<StpVersion>({
      service: 'stp',
      access: 'get',
      command: 'get stp version',
    });
    setVersion(ver);
    if ((ver.lan ?? 'none') === 'none') {
      setRoot('');
      setPorts([]);
      return;
    }
    const [rootData, portData] = await Promise.all([
      postWebUiApi<RootBridge>({
        service: 'stp',
        access: 'get',
        command: 'rootbridge',
      }),
      postWebUiApi<StpPortState[]>({
        service: 'stp',
        access: 'get',
        command: 'port state',
      }),
    ]);
    setRoot(rootData.rootbridge ?? '');
    setPorts(portData ?? []);
  }, []);

  React.useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        await reload();
        if (alive) setError(null);
      } catch (err) {
        if (alive) setError((err as Error).message);
      }
    };
    load().catch(() => {});
    const timer = window.setInterval(() => load().catch(() => {}), 3000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [reload]);

  const stpEnabled = (version.lan ?? 'none') !== 'none';

  return (
    <WebUiPageWrapper titleList={['Status', 'Spanning Tree']}>
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="flex flex-row gap-4">
        <div className="flex w-1/3 flex-col gap-2">
          <h4 className="font-bold">Current Configuration</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <td>Parameter</td>
                <td>Value</td>
              </tr>
            </thead>
            <tbody>
              <tr className="webui-table-row-content">
                <td>Spanning Tree Protocol</td>
                <td>{version.lan ?? ''}</td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Bridge Priority</td>
                <td>{String(version.lanprio ?? '')}</td>
              </tr>
            </tbody>
          </table>
        </div>
        {stpEnabled ? (
          <>
            <div className="flex w-1/3 flex-col gap-2">
              <h4 className="font-bold">Instance Information</h4>
              <table className="webui-table w-full">
                <thead>
                  <tr className="webui-table-row-head">
                    <td>Parameter</td>
                    <td>Value</td>
                  </tr>
                </thead>
                <tbody>
                  <tr className="webui-table-row-content">
                    <td>Root Bridge ID</td>
                    <td>{root}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="flex w-1/3 flex-col gap-2">
              <h4 className="font-bold">Port Status</h4>
              <table className="webui-table w-full">
                <thead>
                  <tr className="webui-table-row-head">
                    <td>Port</td>
                    <td>State</td>
                    <td>Role</td>
                  </tr>
                </thead>
                <tbody>
                  {ports.map((p) => (
                    <tr
                      key={String(p.port)}
                      className={twMerge(
                        'webui-table-row-content',
                        p.state === 'listening' ? 'bg-blue-100' : '',
                        p.state === 'forwarding' ? 'bg-green-100' : '',
                        p.state === 'blocking' ? 'bg-red-100' : '',
                        p.state === 'disabled' ? 'bg-gray-100' : '',
                      )}
                    >
                      <td>{String(p.port)}</td>
                      <td>{p.state}</td>
                      <td>{p.role?.toLowerCase?.() ?? p.role}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>
    </WebUiPageWrapper>
  );
}
